/**
 * Stale-chunk recovery.
 *
 * Every production promote retires the previous build's hashed chunks: Vercel serves only
 * the promoted deployment, so the old `/assets/*.js` 404. A tab opened before the deploy
 * then gets a rejected import() the first time it opens a lazy sheet or route, React.lazy
 * rethrows it during render, and the whole app unmounts to a white page.
 *
 * Vite reports every rejected dynamic import as a `vite:preloadError` window event. The ones
 * that are failed FETCHES get tagged here (an error thrown while a chunk evaluates is a code
 * bug, and a reload cannot fix it). ChunkErrorBoundary reloads the page once when a tagged
 * error breaks rendering. Nothing else reloads: a failure some code catches is that code's to
 * handle (the landmark-room viewer shows "closed for now" and keeps the planet running), and a
 * background or decorative import that fails mid-run must not throw the run away.
 *
 * A chunk that fails again in the document the reload opened is genuinely missing, so that
 * document never reloads itself, however long its boot took. The boundary offers a Reload
 * button instead.
 */

import { readPinParam } from '../planet/portalBridge.ts';
import { LANDMARK_ID_PATTERN, plotCoords } from './skinContract.ts';

/** sessionStorage key for the time of the last automatic reload. */
export const CHUNK_RELOAD_KEY = 'jubuddy:chunk-reload-at:v1';

/** A document opened within this long of the last automatic reload never reloads itself. */
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

export interface ReloadGuard {
  readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** When this document started loading (performance.timeOrigin). */
  readonly documentStart: number;
  readonly now: () => number;
  readonly reload: () => void;
}

/**
 * Reload unless this document already reloaded, or was itself opened by a reload. Keying on the
 * document rather than on the failure time is what makes a loop impossible: a phone that takes
 * 15 s to reach the missing chunk again would get past any wall-clock cooldown every time.
 * Returns whether it reloaded.
 */
export function reloadOnce({ storage, documentStart, now, reload }: ReloadGuard): boolean {
  // Without storage the reload cannot be remembered, so nothing would stop a loop.
  if (!storage) return false;
  try {
    if (documentStart - Number(storage.getItem(CHUNK_RELOAD_KEY)) < CHUNK_RELOAD_COOLDOWN_MS) return false;
    storage.setItem(CHUNK_RELOAD_KEY, String(now()));
  } catch {
    return false; // blocked or full storage: same as none. The boundary still offers Reload.
  }
  reload();
  return true;
}

/** How a failed module fetch reads in Chrome/Edge, Firefox and Safari. */
const MODULE_FETCH_FAILED = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/;

function isFetchFailure(error: unknown): error is Error {
  if (error instanceof TypeError && MODULE_FETCH_FAILED.test(error.message)) return true;
  return error instanceof Error && error.message.startsWith('Unable to preload CSS'); // Vite's own
}

/** Failed chunk fetches Vite reported, so they can be told apart from code bugs. */
const chunkLoadErrors = new WeakSet<object>();

/** True for a failed chunk fetch that Vite reported: a stale tab, or a genuinely missing chunk. */
export function isChunkLoadError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && chunkLoadErrors.has(error);
}

function sessionStorageOf(win: Window): Storage | null {
  try {
    return win.sessionStorage;
  } catch {
    return null; // a sandboxed iframe throws on access
  }
}

interface PlanetPin { lat: number; lon: number; landmarkId?: string; recovery?: true }

const selectedPlanetPins = new WeakMap<Window, { pin: PlanetPin; scope: string; districtId?: string }>();

function pinScope(win: Window): string {
  const url = new URL(win.location.href);
  return `${url.origin}${url.pathname}?district=${url.searchParams.get('district') ?? ''}`;
}

/** Synchronous: a rejected lazy child can unmount Planet before any effect runs. */
export function rememberPlanetPin(pin: PlanetPin | null, win: Window = window, districtId?: string): void {
  if (!pin || !validPin(pin)) selectedPlanetPins.delete(win);
  else selectedPlanetPins.set(win, { pin: { lat: pin.lat, lon: pin.lon, ...pinSubject(pin.landmarkId, pin) }, scope: pinScope(win), districtId });
}

/** Boundary teardown retains the selection; leaving Planet clears it before a return. */
export function clearPlanetPinOnNavigation(win: Window = window): () => void {
  const pathname = win.location.pathname;
  return () => {
    if (win.location.pathname !== pathname) rememberPlanetPin(null, win);
  };
}

function validPin(pin: { lat: number; lon: number }): boolean {
  return Number.isFinite(pin.lat) && Number.isFinite(pin.lon) && Math.abs(pin.lat) <= 90 && Math.abs(pin.lon) <= 180;
}

/** Building anchors round to five decimals; landmarks are checked against the live scene on arrival. */
function pinSubject(id: string | null | undefined, pin: PlanetPin): { landmarkId?: string } {
  if (!id || id.length > 96) return {};
  if (LANDMARK_ID_PATTERN.test(id)) return { landmarkId: id };
  const building = /^bld:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/.exec(id);
  const anchor = building ? { lon: Number(building[1]), lat: Number(building[2]) } : plotCoords(id);
  return anchor && validPin(anchor) && anchor.lon.toFixed(5) === pin.lon.toFixed(5) && anchor.lat.toFixed(5) === pin.lat.toFixed(5)
    ? { landmarkId: id } : {};
}

/** Recovery is a revisit, so Planet consumes this separately from a new portal scan. */
export function readChunkPin(search = typeof window === 'undefined' ? '' : window.location.search): (PlanetPin & { recovery: true }) | null {
  const params = new URLSearchParams(search);
  const parts = params.get('chunkPin')?.split(',');
  if (!parts || parts.length !== 2 || parts.some((part) => !part.trim())) return null;
  const pin = { lat: Number(parts[0]), lon: Number(parts[1]) };
  return validPin(pin) ? { ...pin, ...pinSubject(params.get('chunkSubject'), pin), recovery: true } : null;
}

/** The explicit portal pin wins; recovery carries its no-reward marker. */
export function readPendingPlanetPin(
  search = typeof window === 'undefined' ? '' : window.location.search,
): PlanetPin | null {
  return readPinParam(search) ?? readChunkPin(search);
}

function preservePlanetPin(win: Window): void {
  const selected = selectedPlanetPins.get(win);
  if (!selected) return;
  if (selected.scope !== pinScope(win)) {
    selectedPlanetPins.delete(win);
    return;
  }
  const url = new URL(win.location.href);
  if (!url.searchParams.has('district') && selected.districtId) url.searchParams.set('district', selected.districtId);
  url.searchParams.set('chunkPin', `${selected.pin.lat},${selected.pin.lon}`);
  if (selected.pin.landmarkId) url.searchParams.set('chunkSubject', selected.pin.landmarkId);
  else url.searchParams.delete('chunkSubject');
  win.history.replaceState(win.history.state, '', url.href);
}

/** Manual retry keeps the same pin even when the automatic loop guard held. */
export function reloadChunkErrorManually(win: Window = window): void {
  preservePlanetPin(win);
  win.location.reload();
}

/** The guarded reload for a failed chunk load. Returns whether it reloaded. */
export function reloadForChunkError(win: Window = window): boolean {
  // Offline, a reload would swap the app for the browser's error page. Keep the Reload prompt.
  if (win.navigator.onLine === false) return false;
  return reloadOnce({
    storage: sessionStorageOf(win),
    documentStart: win.performance.timeOrigin,
    now: Date.now,
    reload: () => {
      preservePlanetPin(win);
      win.location.reload();
    },
  });
}

/**
 * Call once from the app entry, before the first render. It never calls preventDefault: Vite
 * then rethrows the error, so the code that asked for the chunk (or ChunkErrorBoundary) sees it.
 */
export function trackChunkLoadErrors(win: Window): void {
  win.addEventListener('vite:preloadError', (event) => {
    const { payload } = event as Event & { payload?: unknown };
    if (isFetchFailure(payload)) chunkLoadErrors.add(payload);
  });
}
